import { z } from "zod";
import { adminOperationsExtendedSchemaVersionSchema, adminSafeIdentifierSchema, adminUlidSchema, } from "./admin-operations-core.js";
import { adminFulfillmentCaseV1Schema } from "./fulfillment.js";
import { simulatedPrintFulfillmentSchema } from "./simulated-print.js";
/** Owners read without a case; the view is in Action history (2026-11-02). */
export const adminFulfillmentRequestSchema = z.union([
    z.object({ caseId: adminUlidSchema.optional(), orderId: adminSafeIdentifierSchema }).strict(),
    z.object({ caseId: adminUlidSchema.optional(), bookId: adminSafeIdentifierSchema }).strict(),
]);
export const adminFulfillmentUnavailableSchema = z
    .object({
    schemaVersion: adminOperationsExtendedSchemaVersionSchema,
    availability: z.literal("UNAVAILABLE"),
    reasonCode: z.literal("LC92_FULFILLMENT_INACTIVE"),
    orderId: adminSafeIdentifierSchema,
    message: z.literal("Fulfillment operations are installed but inactive for this stage."),
})
    .strict();
export const adminFulfillmentAvailableSchema = z
    .object({
    schemaVersion: adminOperationsExtendedSchemaVersionSchema,
    availability: z.literal("AVAILABLE"),
    orderId: adminSafeIdentifierSchema,
    fulfillment: adminFulfillmentCaseV1Schema,
})
    .strict();
/**
 * Content-free projection of the durable shared TEST gift simulator record.
 * This intentionally remains a different response kind from an LC-92 physical
 * order: it has no payment, provider order, carrier, or physical shipment.
 */
export const adminSimulatedPrintAvailableSchema = z
    .object({
    schemaVersion: adminOperationsExtendedSchemaVersionSchema,
    availability: z.literal("SIMULATED_PRINT_AVAILABLE"),
    source: z.literal("DURABLE_SHARED_TEST_GIFT_SIMULATOR"),
    bookId: adminSafeIdentifierSchema,
    fulfillment: simulatedPrintFulfillmentSchema,
})
    .strict();
export const adminFulfillmentSummarySchema = z.discriminatedUnion("availability", [
    adminFulfillmentAvailableSchema,
    adminSimulatedPrintAvailableSchema,
    adminFulfillmentUnavailableSchema,
]);
export const adminFulfillmentResponseSchema = z
    .object({ data: adminFulfillmentSummarySchema })
    .strict();
/**
 * Recent print orders, newest first (admin audit, 2026-10-15): before, staff could find a print
 * order only by its full id inside a case. The queue carries no customer details; opening an
 * order still goes through a case.
 */
export const adminPrintOrderQueueRequestSchema = z
    .object({
    limit: z.number().int().min(1).max(50).default(25),
    cursor: z.string().min(1).max(512).optional(),
    /**
     * One order by its full id or its short reference, the id's last eight characters as staff
     * and customers quote it (stage 2, 2026-11-02). A leading "#" and case are ignored.
     */
    reference: z
        .string()
        .trim()
        .transform((value) => value.replace(/^#/u, "").toUpperCase())
        .pipe(z.string().regex(/^(?:[0-9A-HJKMNP-TV-Z]{8}|[0-9A-HJKMNP-TV-Z]{26})$/u))
        .optional(),
})
    .strict()
    .refine((request) => !(request.reference && request.cursor), {
    message: "Find one order by its reference, or page through the list, not both.",
});
export const adminPrintOrderQueueEntrySchema = z
    .object({
    physicalOrderId: adminUlidSchema,
    /** The short reference the customer sees on their order page and in emails. */
    reference: z.string().regex(/^[0-9A-HJKMNP-TV-Z]{8}$/u),
    orderedAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    /**
     * When the order entered its current state (stage 2 follow-up A8): the recorded state time,
     * or, for an order still in the state it was made in, when it was made. Missing only for an
     * order whose state last changed before state times were recorded (2026-10-17): `updatedAt`
     * is not a stand-in, since a check that found nothing new used to move it.
     */
    stateChangedAt: z.string().datetime().optional(),
    /** The order's printing state, or UNREADABLE when its records can't be read. */
    state: z.string().regex(/^[A-Z][A-Z_]{1,63}$/u),
    needsAttention: z.boolean(),
    /** A simulated order, or one sent to the printer's sandbox: no book is printed. */
    test: z.boolean(),
    copies: z.number().int().min(1).max(20).optional(),
})
    .strict();
export const adminPrintOrderQueueResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminOperationsExtendedSchemaVersionSchema,
        orders: z.array(adminPrintOrderQueueEntrySchema).max(50),
        nextCursor: z.string().min(1).max(512).optional(),
    })
        .strict(),
})
    .strict();
//# sourceMappingURL=admin-operations-fulfillment.js.map